// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SparseOrganization } from '@/account/api/types';
import { UpdateOrganizationParameters } from '@/account/api/update-organization';
import { useUpdateOrganization } from '@/account/hooks/use-update-organization';
import { FormCard } from '@/components/form-card';
import { FormCurrencySelect } from '@/components/form-currency-select';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import { orgDefaultCurrencyCodeSchema } from '@/constants/currency';
import { PaymentTerm, paymentTermSchema } from '@/payment-term/api/types';
import { PaymentTermSearch } from '@/payment-term/components/payment-term-search';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Controller,
  SubmitHandler,
  useController,
  useForm,
} from 'react-hook-form';
import { z } from 'zod';

// Form holds the WACC as a percentage string ("12" for 12%) — that's what the
// user types in. We convert to/from the wire format (decimal in [0.01, 1.00])
// at the form boundary so the on-screen unit (%) matches the input.
const WACC_PERCENT_PATTERN = /^\d{1,3}(\.\d{1,2})?$/;
const WACC_MIN_PERCENT = 1;
const WACC_MAX_PERCENT = 100;

const formSchema = z.object({
  billingAddress: z.string(),
  defaultPaymentTerm: paymentTermSchema.nullable(),
  defaultInvoiceMemo: z.string(),
  defaultPaymentInstructions: z.string(),
  invoiceNumberPrefix: z.string(),
  defaultCurrency: orgDefaultCurrencyCodeSchema,
  weightedAverageCostOfCapitalPercent: z
    .string()
    .min(1, 'Cost of capital is required')
    .refine((value) => WACC_PERCENT_PATTERN.test(value), {
      message: 'Enter a percentage between 1 and 100 (up to 2 decimal places)',
    })
    .refine(
      (value) => {
        const asNumber = Number(value);
        return asNumber >= WACC_MIN_PERCENT && asNumber <= WACC_MAX_PERCENT;
      },
      {
        message: 'Cost of capital must be between 1% and 100%',
      }
    ),
});

/**
 * Converts the decimal WACC the API returns (e.g. "0.1234") into the
 * percentage string the form displays (e.g. "12.34"). Trailing zeros after
 * the decimal point are trimmed so `0.1200` renders as "12" rather than
 * "12.00", matching how a user would naturally type it.
 */
function decimalToPercentString(decimalString: string): string {
  const asNumber = Number(decimalString);
  if (Number.isNaN(asNumber)) {
    return '';
  }
  const percent = asNumber * 100;
  // Round to 2 decimal places to absorb floating-point noise, then drop
  // trailing zeros (and the decimal point if integer-valued).
  return percent.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Converts the percentage string the form holds (e.g. "12.34") into the
 * decimal string the API expects (e.g. "0.1234"). Round-trips the precision
 * window the form schema accepts (4 decimal places of fractional WACC).
 */
function percentStringToDecimal(percentString: string): string {
  const asNumber = Number(percentString);
  if (Number.isNaN(asNumber)) {
    return '0';
  }
  return (asNumber / 100).toFixed(4);
}

type FormData = z.infer<typeof formSchema>;

interface Props {
  organization: SparseOrganization;
}

function emptyStringToNull(value: string): string | null {
  return value.trim() === '' ? null : value;
}

export function OrganizationBillingDefaultsCard(props: Props) {
  const updateOrganizationMutation = useUpdateOrganization();

  const { control, formState, handleSubmit, reset } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      billingAddress: props.organization.billingAddress ?? '',
      defaultPaymentTerm: props.organization.defaultPaymentTerm,
      defaultInvoiceMemo: props.organization.defaultInvoiceMemo ?? '',
      defaultPaymentInstructions:
        props.organization.defaultPaymentInstructions ?? '',
      invoiceNumberPrefix: props.organization.invoiceNumberPrefix ?? '',
      defaultCurrency: props.organization.defaultCurrency,
      weightedAverageCostOfCapitalPercent: decimalToPercentString(
        props.organization.weightedAverageCostOfCapital
      ),
    },
  });

  const defaultPaymentTermController = useController({
    control,
    name: 'defaultPaymentTerm',
  });

  const isSaveDisabled =
    updateOrganizationMutation.isPending || !formState.isDirty;

  const handleReset = () => {
    reset();
  };

  const handleChangePaymentTerm = (value: PaymentTerm | null) => {
    defaultPaymentTermController.field.onChange(value);
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    const parameters: UpdateOrganizationParameters = {};

    if (formState.dirtyFields.billingAddress) {
      parameters.billingAddress = emptyStringToNull(data.billingAddress);
    }

    if (formState.dirtyFields.defaultPaymentTerm) {
      parameters.defaultPaymentTerm = data.defaultPaymentTerm?.id ?? null;
    }

    if (formState.dirtyFields.defaultInvoiceMemo) {
      parameters.defaultInvoiceMemo = emptyStringToNull(
        data.defaultInvoiceMemo
      );
    }

    if (formState.dirtyFields.defaultPaymentInstructions) {
      parameters.defaultPaymentInstructions = emptyStringToNull(
        data.defaultPaymentInstructions
      );
    }

    if (formState.dirtyFields.invoiceNumberPrefix) {
      parameters.invoiceNumberPrefix = emptyStringToNull(
        data.invoiceNumberPrefix
      );
    }

    if (formState.dirtyFields.defaultCurrency) {
      parameters.defaultCurrency = data.defaultCurrency;
    }

    if (formState.dirtyFields.weightedAverageCostOfCapitalPercent) {
      parameters.weightedAverageCostOfCapital = percentStringToDecimal(
        data.weightedAverageCostOfCapitalPercent
      );
    }

    updateOrganizationMutation.mutate(
      { orgKey: props.organization.key, parameters },
      {
        onSuccess: (response) => {
          reset({
            billingAddress: response.organization.billingAddress ?? '',
            defaultPaymentTerm: response.organization.defaultPaymentTerm,
            defaultInvoiceMemo: response.organization.defaultInvoiceMemo ?? '',
            defaultPaymentInstructions:
              response.organization.defaultPaymentInstructions ?? '',
            invoiceNumberPrefix:
              response.organization.invoiceNumberPrefix ?? '',
            defaultCurrency: response.organization.defaultCurrency,
            weightedAverageCostOfCapitalPercent: decimalToPercentString(
              response.organization.weightedAverageCostOfCapital
            ),
          });
        },
      }
    );
  };

  return (
    <FormCard
      title="Billing and invoicing"
      isDirty={formState.isDirty}
      isDisabled={isSaveDisabled}
      onReset={handleReset}
      onSubmit={handleSubmit(handleValidSubmission)}
    >
      <SupportSnackbar
        isOpen={updateOrganizationMutation.isError}
        message="An error occurred while updating invoicing defaults."
        onClose={updateOrganizationMutation.reset}
      />
      <Stack spacing={2} padding={2}>
        <Typography variant="body2" color="text.secondary">
          These defaults are copied into new invoices unless a project or
          invoice overrides them
        </Typography>
        <Divider />
        <Stack spacing={2}>
          <Typography variant="overline" color="text.secondary">
            Invoice header
          </Typography>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Invoice number prefix</Typography>
            <Typography variant="body2" color="text.secondary">
              Appears at the top of the invoice and is used for future
              auto-generated invoice numbers (leave blank to use <b>INV</b>,
              such as INV-2026-0001)
            </Typography>
            <Box width={{ xs: '100%', md: 240 }}>
              <FormTextField
                name="invoiceNumberPrefix"
                label="Invoice number prefix"
                control={control}
                disabled={updateOrganizationMutation.isPending}
                fullWidth
              />
            </Box>
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Payment terms</Typography>
            <Typography variant="body2" color="text.secondary">
              Default due-date terms for invoices when the project does not
              specify one
            </Typography>

            <Box width={{ xs: '100%', md: 480 }}>
              <PaymentTermSearch
                label="Default payment term"
                value={defaultPaymentTermController.field.value}
                onChange={handleChangePaymentTerm}
                disabled={updateOrganizationMutation.isPending}
              />
            </Box>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2">Bill-from address</Typography>
            <Typography variant="body2" color="text.secondary">
              Shown in the invoice header with your organization name.
            </Typography>
            <FormTextField
              name="billingAddress"
              label="Billing address"
              control={control}
              disabled={updateOrganizationMutation.isPending}
              fullWidth
              isMultiline
              minimumRows={4}
              sx={{ width: { xs: '100%', md: 480 } }}
            />
          </Stack>
        </Stack>
        <Divider />
        <Stack spacing={2}>
          <Typography variant="overline" color="text.secondary">
            Additional information
          </Typography>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems="flex-start"
          >
            <Stack spacing={1} width={{ xs: '100%', md: 480 }}>
              <Typography variant="subtitle2">Invoice memo</Typography>
              <Typography variant="body2" color="text.secondary">
                Customer-facing notes copied into the invoice memo section
              </Typography>
              <FormTextField
                name="defaultInvoiceMemo"
                label="Default invoice memo"
                control={control}
                disabled={updateOrganizationMutation.isPending}
                fullWidth
                isMultiline
                minimumRows={5}
              />
            </Stack>
            <Stack spacing={1} width={{ xs: '100%', md: 480 }}>
              <Typography variant="subtitle2">Payment instructions</Typography>
              <Typography variant="body2" color="text.secondary">
                Remittance details copied into the payment instructions section
              </Typography>
              <FormTextField
                name="defaultPaymentInstructions"
                label="Default payment instructions"
                control={control}
                disabled={updateOrganizationMutation.isPending}
                fullWidth
                isMultiline
                minimumRows={5}
              />
            </Stack>
          </Stack>
        </Stack>
        <Divider />
        <Stack spacing={2}>
          <Typography variant="overline" color="text.secondary">
            Reporting and analytics
          </Typography>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems="flex-start"
          >
            <Stack spacing={1} width={{ xs: '100%', md: 480 }}>
              <Typography variant="subtitle2">Default currency</Typography>
              <Typography variant="body2" color="text.secondary">
                Default currency for your organization
              </Typography>
              <Box width={{ xs: '100%', md: 240 }} sx={{ pt: 1 }}>
                <FormCurrencySelect
                  control={control}
                  name="defaultCurrency"
                  label="Default currency"
                  fullWidth
                  disabled={updateOrganizationMutation.isPending}
                />
              </Box>
            </Stack>

            <Stack spacing={1} width={{ xs: '100%', md: 480 }}>
              <Typography variant="subtitle2">
                Weighted average cost of capital
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cost of capital used by the billing-lifecycle report to compute
                carrying cost
              </Typography>
              <Box width={{ xs: '100%', md: 240 }} sx={{ pt: 1 }}>
                <Controller
                  control={control}
                  name="weightedAverageCostOfCapitalPercent"
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="WACC"
                      size="small"
                      fullWidth
                      disabled={updateOrganizationMutation.isPending}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      inputProps={{
                        inputMode: 'decimal',
                        style: { textAlign: 'right' },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </FormCard>
  );
}
